import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { featuresApi } from "../api/featuresApi";
import { useFeaturesData } from "../hooks/useFeaturesData";
import FeaturesTable from "../components/FeaturesTable";
import FeatureDrawer from "../components/FeatureDrawer";
import SubFeatureDrawer from "../components/SubFeatureDrawer";
import ExcelImportDrawer from "../components/ExcelImportDrawer";
import ProductDrawer from "../../products/components/ProductDrawer";
import { ASSET_OPTIONS } from "../constants/featuresConstants";
import "../../../V2SideDrawer.css";
import "../styles/FeaturesViewV2Page.css";

export default function FeaturesViewV2Page({ user, showToast }) {
  const isAdmin = ["Admin", "Super Admin"].includes(user?.db_role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );

  const [pageTab, setPageTab] = useState("features");

  const {
    features,
    totalFeatures,
    products,
    subFeatures,
    subLoading,
    loading,
    filters,
    setFilters,
    loadSubsFor,
    refetchSubsFor,
    refetch,
    refetchProducts,
  } = useFeaturesData();

  const [expanded, setExpanded] = useState(() => new Set());
  const [featureDrawer, setFeatureDrawer] = useState(null);
  const [subDrawer, setSubDrawer] = useState(null);
  const [showImport, setShowImport] = useState(false);

  // Products tab state
  const [productSearch, setProductSearch] = useState("");
  const [productDrawer, setProductDrawer] = useState(null); // null | { mode, product }

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  // ── Deep-link auto-open via ?id= / ?sub_id= ─────────────────────────────────
  // Other modules (Defects ref-link, Tasks links) build URLs like:
  //   /features?id=<feature-uuid>
  //   /features?sub_id=<sub-feature-uuid>
  // On load we resolve those into an open drawer, then strip the query string
  // so a page refresh doesn't re-pop the drawer.
  useEffect(() => {
    const featureId = searchParams.get("id");
    const subId     = searchParams.get("sub_id");
    if (!featureId && !subId) return;

    let cancelled = false;

    if (featureId && features.length > 0) {
      const feat = features.find((f) => f.id === featureId);
      if (feat) {
        setFeatureDrawer({ mode: "edit", data: feat });
        setSearchParams({}, { replace: true });
      }
    }

    if (subId) {
      featuresApi.getSubFeature(subId)
        .then((sf) => {
          if (cancelled || !sf) return;
          setSubDrawer({ mode: "edit", data: sf, featureId: sf.feature_id });
          setSearchParams({}, { replace: true });
        })
        .catch(() => {});
    }

    return () => { cancelled = true; };
  }, [features, searchParams, setSearchParams]);

  function toggleExpand(featureId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) {
        next.delete(featureId);
      } else {
        next.add(featureId);
        loadSubsFor(featureId);
      }
      return next;
    });
  }

  function openFeatureEdit(feature) {
    if (!isAdmin) return;
    setFeatureDrawer({ mode: "edit", data: feature });
  }

  function openSubEdit(sf, featureId) {
    if (!isAdmin) return;
    setSubDrawer({ mode: "edit", data: sf, featureId });
  }

  function openNewFeature() {
    if (!isAdmin) return;
    setFeatureDrawer({ mode: "create", data: null });
  }

  function openNewSub(featureId) {
    if (!isAdmin) return;
    setSubDrawer({ mode: "create", data: null, featureId });
  }

  async function handleFeatureSaved() {
    setFeatureDrawer(null);
    await refetch();
  }

  async function handleSubSaved() {
    const fid = subDrawer?.featureId;
    setSubDrawer(null);
    if (fid) await refetchSubsFor(fid);
  }

  async function handleDeleteFeature(feature) {
    if (!isAdmin) return;
    const ok = window.confirm(
      `Delete feature "${feature.feature_name || feature.feature_code}" and all its sub-features?`
    );
    if (!ok) return;
    try {
      await featuresApi.deleteFeature(feature.id);
      showToast?.("Feature deleted", "success");
      await refetch();
    } catch (err) {
      showToast?.(err?.message || "Failed to delete feature", "error");
    }
  }

  async function handleDeleteSub(sf, featureId) {
    if (!isAdmin) return;
    const ok = window.confirm(`Delete sub-feature "${sf.sub_feature_name || sf.sub_feature_code}"?`);
    if (!ok) return;
    try {
      await featuresApi.deleteSubFeature(sf.id);
      showToast?.("Sub-feature deleted", "success");
      await refetchSubsFor(featureId);
    } catch (err) {
      showToast?.(err?.message || "Failed to delete sub-feature", "error");
    }
  }

  return (
    <div
      className="ftv2-shell"
      style={{
        gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr`,
      }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => {
          setSidebarCollapsed((c) => {
            const next = !c;
            localStorage.setItem("sb-collapsed", String(next));
            return next;
          });
        }}
      />

      <div className="ftv2-main">
        {/* Header */}
        <div className="page-head">
          {pageTab === "features" ? (
            <h1>Features <em>{features.length} of {totalFeatures}</em></h1>
          ) : (
            <h1>Products <em>{filteredProducts.length}</em></h1>
          )}
          <div className="page-head-actions">
            {pageTab === "features" && isAdmin && (
              <button
                className="ftv2-import-btn"
                onClick={() => setShowImport(true)}
                title="Import features from Excel"
              >
                ↑ Import Excel
              </button>
            )}
            {pageTab === "features" && isAdmin && (
              <button className="ftv2-new-btn v2-btn-primary" onClick={openNewFeature}>
                <span className="plus">+</span>
                New Feature
              </button>
            )}
            {pageTab === "products" && isAdmin && (
              <button
                className="ftv2-new-btn v2-btn-primary"
                onClick={() => setProductDrawer({ mode: "create", product: null })}
              >
                <span className="plus">+</span>
                New Product
              </button>
            )}
          </div>
        </div>

        {/* Unified tab bar + search/filter toolbar */}
        <div className="page-toolbar">
          <button
            type="button"
            className={`page-tab${pageTab === "features" ? " page-tab--active" : ""}`}
            onClick={() => setPageTab("features")}
          >
            Features
            <span className="page-tab-count">{totalFeatures}</span>
          </button>
          <button
            type="button"
            className={`page-tab${pageTab === "products" ? " page-tab--active" : ""}`}
            onClick={() => setPageTab("products")}
          >
            Products
            <span className="page-tab-count">{products.length}</span>
          </button>

          <div className="page-toolbar-right">
            {pageTab === "features" && (
              <>
                <div className="ftv2-toolbar-search">
                  <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                  <input
                    type="text"
                    className="ftv2-search-input"
                    placeholder="Search features…"
                    value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                  />
                </div>
                {products.length > 0 && (
                  <select
                    className="ftv2-filter-select"
                    value={filters.product_id}
                    onChange={(e) => setFilters((p) => ({ ...p, product_id: e.target.value }))}
                  >
                    <option value="">All products</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                <select
                  className="ftv2-filter-select"
                  value={filters.asset}
                  onChange={(e) => setFilters((p) => ({ ...p, asset: e.target.value }))}
                >
                  <option value="">All assets</option>
                  {ASSET_OPTIONS.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </>
            )}
            {pageTab === "products" && (
              <div className="ftv2-toolbar-search">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  type="text"
                  className="ftv2-search-input"
                  placeholder="Search products…"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── FEATURES TAB ── */}
        {pageTab === "features" && (
          <FeaturesTable
            features={features}
            loading={loading}
            expanded={expanded}
            subFeatures={subFeatures}
            subLoading={subLoading}
            onToggleExpand={toggleExpand}
            onOpenFeature={openFeatureEdit}
            onOpenSubFeature={openSubEdit}
            onAddSubFeature={isAdmin ? openNewSub : null}
            onDeleteFeature={isAdmin ? handleDeleteFeature : null}
            onDeleteSubFeature={isAdmin ? handleDeleteSub : null}
          />
        )}

        {/* ── PRODUCTS TAB ── */}
        {pageTab === "products" && (
          <div className="page-table-wrap">
              <table className="page-table ftv2-products-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={3} className="ftv2-products-empty">
                        {productSearch ? "No products match your search." : "No products yet."}
                      </td>
                    </tr>
                  )}
                  {filteredProducts.map((p) => (
                    <tr
                      key={p.id}
                      className="ftv2-row--feature ftv2-product-row"
                      onClick={() => setProductDrawer({ mode: "edit", product: p })}
                    >
                      <td>
                        <span className="ftv2-product-name">{p.name}</span>
                      </td>
                      <td className="ftv2-product-desc">
                        {p.description || <span className="ftv2-product-nodesc">—</span>}
                      </td>
                      <td className="ftv2-product-date">
                        {p.created_at
                          ? new Date(p.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
      </div>

      {featureDrawer && (
        <FeatureDrawer
          mode={featureDrawer.mode}
          feature={featureDrawer.data}
          products={products}
          onClose={() => setFeatureDrawer(null)}
          onSaved={handleFeatureSaved}
          showToast={showToast}
        />
      )}

      {subDrawer && (
        <SubFeatureDrawer
          mode={subDrawer.mode}
          subFeature={subDrawer.data}
          featureId={subDrawer.featureId}
          onClose={() => setSubDrawer(null)}
          onSaved={handleSubSaved}
          showToast={showToast}
        />
      )}

      {showImport && (
        <ExcelImportDrawer
          onClose={() => setShowImport(false)}
          onImported={() => { refetch(); }}
          showToast={showToast}
        />
      )}

      {productDrawer && (
        <ProductDrawer
          mode={productDrawer.mode}
          product={productDrawer.product}
          isAdmin={isAdmin}
          onClose={() => setProductDrawer(null)}
          onSaved={async () => { setProductDrawer(null); await refetchProducts(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}
